import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { ensureTaxRepositoryConfigured } from "@/lib/taxRepository";
import { PageHeader } from "./layoutChrome";
import LaboralFlujoOperativo from "@/components/laboral/LaboralFlujoOperativo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, FolderOpen, Info, Loader2, Plus, Trash2, UserPlus, ExternalLink, CheckCircle2, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { IdentityValidateButton } from "@/components/identity/IdentityCheckDialog";

const INDICADORES = [
  { value: "1", label: "1 — Empresarial asimilable ≤ $400,000" },
  { value: "2", label: "2 — Empresarial asimilable > $400,000" },
  { value: "3", label: "3 — Asimilable a salarios ≤ $400,000" },
  { value: "4", label: "4 — Asimilable a salarios > $400,000" },
  { value: "5", label: "5 — Asalariado ≤ $400,000 (habitual)" },
  { value: "6", label: "6 — Asalariado > $400,000" },
];

const SAT_PORTAL_FALLBACK_URL = "https://wwwmat.sat.gob.mx/portal/home/inicio";

const INSTRUCCIONES_PORTAL_DEFAULT = [
  "Paso 1: Inicie sesión en el portal del SAT con la e.firma del Patrón.",
  "Paso 2: Diríjase a la sección de presentación de la solicitud.",
  "Paso 3: Al solicitar el archivo, pegue la ruta que ya está en su portapapeles o busque el archivo .txt generado en su carpeta local BNT.TaxRepository.",
  "Paso 4: Envíe la solicitud, resuelva el captcha manual y capture el Acuse de recibo en nuestro sistema.",
];

function formatDateMx(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function emptyForm() {
  return {
    curp: "",
    apellido_paterno: "",
    apellido_materno: "",
    nombres: "",
    fecha_ingreso: new Date().toISOString().slice(0, 10),
  };
}

/**
 * Inscripción masiva de trabajadores sin RFC — genera TXT layout SAT.
 */
export default function InscripcionTrabajadoresSat({ embedMode } = {}) {
  const isTramitesEmbed = embedMode === "tramites";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [repoConfig, setRepoConfig] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [workers, setWorkers] = useState([]);
  const [indicador, setIndicador] = useState("5");
  const [correo, setCorreo] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyCurp, setBusyCurp] = useState("");
  const [success, setSuccess] = useState(null);
  /** Paso 1 completado: TXT generado y ruta en portapapeles → habilita botón 2. */
  const [txtGenerated, setTxtGenerated] = useState(false);
  const [generatedTxt, setGeneratedTxt] = useState(null);
  const [splitScreenOpen, setSplitScreenOpen] = useState(false);

  const cli = useMemo(() => clients.find((c) => c.id === clientId), [clients, clientId]);
  const portalUrl = repoConfig?.sat_portal_url || SAT_PORTAL_FALLBACK_URL;
  const instruccionesPortal = repoConfig?.instrucciones_portal?.length
    ? repoConfig.instrucciones_portal
    : INSTRUCCIONES_PORTAL_DEFAULT;

  const resetFlujoInscripcion = () => {
    setTxtGenerated(false);
    setGeneratedTxt(null);
    setSplitScreenOpen(false);
  };

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (!clientId) {
      setRepoConfig(null);
      resetFlujoInscripcion();
      return;
    }
    resetFlujoInscripcion();
    (async () => {
      try {
        const { data } = await api.get("/sat/trabajadores-inscripcion/config", {
          params: { client_id: clientId },
        });
        setRepoConfig(data);
      } catch {
        setRepoConfig(null);
      }
    })();
  }, [clientId]);

  const onAddWorker = async () => {
    if (!clientId) {
      toast.error("Seleccione un cliente (patrón)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/sat/trabajadores-inscripcion/validar", {
        curp: form.curp,
        apellido_paterno: form.apellido_paterno,
        apellido_materno: form.apellido_materno,
        nombres: form.nombres,
        fecha_ingreso: form.fecha_ingreso,
      });
      const w = data.worker;
      if (workers.some((row) => row.curp === w.curp)) {
        toast.error("Esta CURP ya está en la lista");
        return;
      }
      setWorkers((prev) => [...prev, { ...w, id: w.curp }]);
      setForm(emptyForm());
      toast.success("Trabajador agregado a la lista");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Datos incompletos o CURP inválida");
    } finally {
      setBusy(false);
    }
  };

  const onRemoveWorker = (id) => {
    setWorkers((prev) => prev.filter((w) => w.id !== id));
  };

  useEffect(() => {
    resetFlujoInscripcion();
  }, [workers, indicador, correo]);

  const copyPathToClipboard = async (path) => {
    if (!path) return false;
    try {
      await navigator.clipboard.writeText(path);
      return true;
    } catch {
      return false;
    }
  };

  /** Botón 1: genera .txt SAT, guarda en BNT.TaxRepository y copia ruta al portapapeles. */
  const onGenerateTxt = async () => {
    if (!clientId) {
      toast.error("Seleccione un cliente");
      return;
    }
    if (!workers.length) {
      toast.error("Agregue al menos un trabajador a la lista");
      return;
    }
    if (!correo.trim()) {
      toast.error("Indique el correo electrónico para el trámite SAT");
      return;
    }

    setBusy(true);
    resetFlujoInscripcion();
    try {
      if (!repoConfig?.configured) {
        await ensureTaxRepositoryConfigured({
          clientId,
          dateStr: new Date().toISOString().slice(0, 10),
          direccion: "emitido",
        });
        const { data: cfg } = await api.get("/sat/trabajadores-inscripcion/config", {
          params: { client_id: clientId },
        });
        setRepoConfig(cfg);
      }

      const { data } = await api.post("/sat/trabajadores-inscripcion/generar", {
        client_id: clientId,
        workers: workers.map(({ curp, apellido_paterno, apellido_materno, nombres, fecha_ingreso }) => ({
          curp,
          apellido_paterno,
          apellido_materno,
          nombres,
          fecha_ingreso,
        })),
        indicador_ingresos: indicador,
        correo: correo.trim(),
        generate_conformity_letters: false,
      });

      const fullPath = data.file_path || data.folder_path;
      const copied = await copyPathToClipboard(fullPath);
      setGeneratedTxt(data);
      setTxtGenerated(true);
      setSuccess(null);

      toast.success(
        copied
          ? `Archivo ${data.filename} generado. Ruta copiada al portapapeles.`
          : `Archivo ${data.filename} generado. Copie la ruta manualmente desde el recuadro verde.`,
        { duration: 6000 },
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar el archivo");
    } finally {
      setBusy(false);
    }
  };

  /** Botón 2: pantalla dividida con instrucciones (izq.) y portal SAT (der.). */
  const onOpenPortalSplit = () => {
    if (!txtGenerated) return;
    setSplitScreenOpen(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const onCopyFolder = async () => {
    const path =
      generatedTxt?.file_path ||
      success?.file_path ||
      success?.folder_path ||
      success?.RUTA_ARCHIVOS_GENERALES;
    if (!path) return;
    const ok = await copyPathToClipboard(path);
    if (ok) toast.success("Ruta copiada al portapapeles");
    else toast.error("No se pudo copiar la ruta");
  };

  const onOpenFolder = async () => {
    if (!clientId) return;
    try {
      await api.post("/sat/trabajadores-inscripcion/abrir-carpeta", null, {
        params: { client_id: clientId },
      });
      toast.success("Carpeta contenedora abierta en el equipo del servidor");
    } catch {
      await onCopyFolder();
      toast.info("No se pudo abrir la carpeta automáticamente; se copió la ruta al portapapeles.");
    }
  };

  const onDownloadFile = async (filename) => {
    const name = filename || success?.filename;
    if (!clientId || !name) return;
    try {
      const { data } = await api.get("/sat/trabajadores-inscripcion/descargar", {
        params: { client_id: clientId, filename: name },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Descarga iniciada: ${name}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar el archivo");
    }
  };

  const onGenerateCarta = async (worker) => {
    if (!clientId) {
      toast.error("Seleccione un cliente");
      return;
    }
    setBusyCurp(worker.curp);
    try {
      if (!repoConfig?.configured) {
        await ensureTaxRepositoryConfigured({
          clientId,
          dateStr: new Date().toISOString().slice(0, 10),
          direccion: "emitido",
        });
        const { data: cfg } = await api.get("/sat/trabajadores-inscripcion/config", {
          params: { client_id: clientId },
        });
        setRepoConfig(cfg);
      }
      const { data } = await api.post("/sat/trabajadores-inscripcion/carta-conformidad", {
        client_id: clientId,
        worker: {
          curp: worker.curp,
          apellido_paterno: worker.apellido_paterno,
          apellido_materno: worker.apellido_materno,
          nombres: worker.nombres,
          fecha_ingreso: worker.fecha_ingreso,
        },
      });
      toast.success(data.message || "Carta de conformidad generada");
      setSuccess({
        ...data,
        conformity_letters: [data],
        conformity_letter_count: 1,
        worker_count: 1,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar la carta");
    } finally {
      setBusyCurp("");
    }
  };

  const panelBody = (
      <div className="p-8 space-y-5 max-w-7xl mx-auto">
        <div className="rounded-sm border border-sky-200 bg-sky-50/90 px-4 py-3 text-sm text-slate-800 leading-relaxed flex gap-3">
          <Info className="w-5 h-5 shrink-0 text-sky-700 mt-0.5" />
          <p className="m-0">
            El archivo TXT y las cartas PDF se guardan en <strong>BNT.TaxRepository/[RFC]/Archivos Generales/</strong>.
            Layout SAT: campos separados por pipe (<code>|</code>), mayúsculas y codificación UTF-8.
            Cada trabajador genera <strong>Carta_Conformidad_[CURP].pdf</strong> con el texto legal de autorización.
            Después comprima el .txt en ZIP y adjúntelo en Mi Portal SAT (trámite INSC_ASALARIADO).
          </p>
        </div>

        <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Cliente (patrón)</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="mt-1 h-auto min-h-[40px]">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.razon_social} — {c.rfc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-[11px] text-slate-600 rounded border border-slate-200 bg-slate-50 p-3">
              <div><strong>RFC patrón:</strong> {cli?.rfc || "—"}</div>
              <div className="mt-1 break-all">
                <strong>Archivos Generales:</strong>{" "}
                {repoConfig?.RUTA_ARCHIVOS_GENERALES || "Configure BNT.TaxRepository al generar"}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-[#0F2D5C]">
            <UserPlus className="w-4 h-4" /> Captura de trabajador
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">CURP (18 caracteres)</Label>
              <div className="flex gap-2 items-center mt-1">
                <Input
                  value={form.curp}
                  onChange={(e) => setForm((f) => ({ ...f, curp: e.target.value.toUpperCase() }))}
                  maxLength={18}
                  className="h-9 text-xs font-mono uppercase flex-1"
                  placeholder="XXXX000000HDFXXX00"
                />
                <IdentityValidateButton curp={form.curp} label="Validar CURP" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Apellido paterno</Label>
              <Input
                value={form.apellido_paterno}
                onChange={(e) => setForm((f) => ({ ...f, apellido_paterno: e.target.value }))}
                className="mt-1 h-9 text-xs uppercase"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Apellido materno (opcional)</Label>
              <Input
                value={form.apellido_materno}
                onChange={(e) => setForm((f) => ({ ...f, apellido_materno: e.target.value }))}
                className="mt-1 h-9 text-xs uppercase"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Nombre(s)</Label>
              <Input
                value={form.nombres}
                onChange={(e) => setForm((f) => ({ ...f, nombres: e.target.value }))}
                className="mt-1 h-9 text-xs uppercase"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Fecha de ingreso</Label>
              <Input
                type="date"
                value={form.fecha_ingreso}
                onChange={(e) => setForm((f) => ({ ...f, fecha_ingreso: e.target.value }))}
                className="mt-1 h-9 text-xs"
              />
            </div>
          </div>
          <Button type="button" onClick={onAddWorker} disabled={busy || !clientId} className="bg-[#0F2D5C] hover:bg-[#0a2247]">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Agregar a la lista
          </Button>
        </div>

        <div className="bg-white rounded-md border border-slate-200 p-5 space-y-3">
          <div className="text-sm font-bold text-[#0F2D5C]">Lista de trabajadores ({workers.length})</div>
          <div className="overflow-x-auto rounded border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>CURP</TableHead>
                  <TableHead>Ap. paterno</TableHead>
                  <TableHead>Ap. materno</TableHead>
                  <TableHead>Nombre(s)</TableHead>
                  <TableHead>Fecha ingreso</TableHead>
                  <TableHead className="w-28 text-center">Carta PDF</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workers.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell className="font-mono text-[11px]">{w.curp}</TableCell>
                    <TableCell className="text-xs">{w.apellido_paterno}</TableCell>
                    <TableCell className="text-xs">{w.apellido_materno || "—"}</TableCell>
                    <TableCell className="text-xs">{w.nombres}</TableCell>
                    <TableCell className="text-xs">{formatDateMx(w.fecha_ingreso)}</TableCell>
                    <TableCell className="text-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-[10px] px-2"
                        disabled={Boolean(busyCurp) || !clientId}
                        onClick={() => onGenerateCarta(w)}
                      >
                        {busyCurp === w.curp ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <>
                            <FileText className="w-3.5 h-3.5 mr-1" />
                            Carta
                          </>
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => onRemoveWorker(w.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!workers.length && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-400 py-8 text-xs">
                      Sin trabajadores en la lista. Capture datos y pulse «Agregar a la lista».
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
          <div className="text-sm font-bold text-[#0F2D5C]">Datos del trámite SAT</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Indicador de ingresos</Label>
              <Select value={indicador} onValueChange={setIndicador}>
                <SelectTrigger className="mt-1 h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INDICADORES.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Correo electrónico (columna 8 SAT)</Label>
              <Input
                type="email"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                className="mt-1 h-9 text-xs"
                placeholder="contacto@empresa.mx"
              />
            </div>
          </div>

          {txtGenerated && generatedTxt ? (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50/90 p-4 space-y-2"
              data-testid="inscripcion-txt-success"
            >
              <div className="flex items-start gap-2 text-emerald-900">
                <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
                <div className="space-y-1 text-sm">
                  <p className="font-semibold m-0">
                    Paso 1 completado — archivo <strong>{generatedTxt.filename}</strong> generado (
                    {generatedTxt.worker_count} trabajador(es)).
                  </p>
                  <p className="text-xs text-emerald-950/80 m-0">
                    La ruta completa del .txt está en su portapapeles. Comprima el archivo en ZIP antes de cargarlo en el SAT.
                  </p>
                  <p className="break-all rounded border border-emerald-200 bg-white p-2 font-mono text-[11px] m-0">
                    {generatedTxt.file_path}
                  </p>
                  <Button type="button" variant="outline" size="sm" className="h-8 mt-1" onClick={onCopyFolder}>
                    Copiar ruta nuevamente
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <Button
              type="button"
              onClick={onGenerateTxt}
              disabled={busy || !clientId || !workers.length || !correo.trim()}
              className="h-12 text-sm font-bold bg-[#0F2D5C] hover:bg-[#0a2247] justify-start px-4"
              data-testid="btn-generar-txt-inscripcion"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" />
              ) : (
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 text-xs mr-2 shrink-0">1</span>
              )}
              Generar Archivo de Inscripción (.txt)
            </Button>
            <Button
              type="button"
              onClick={onOpenPortalSplit}
              disabled={!txtGenerated || busy}
              className="h-12 text-sm font-bold bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 justify-start px-4"
              data-testid="btn-abrir-portal-inscripcion"
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 text-xs mr-2 shrink-0">2</span>
              Abrir Portal y Ver Instrucciones
            </Button>
          </div>
        </div>

        {splitScreenOpen && txtGenerated ? (
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-0 min-h-[70vh] rounded-md border border-slate-300 overflow-hidden shadow-sm"
            data-testid="inscripcion-split-screen"
          >
            <div className="bg-white p-5 space-y-4 border-b lg:border-b-0 lg:border-r border-slate-200">
              <div className="flex items-center gap-2 text-sm font-bold text-[#0F2D5C]">
                <ListOrdered className="w-4 h-4" />
                Instrucciones para el trámite SAT
              </div>
              <ol className="space-y-3 text-sm text-slate-700 list-none m-0 p-0">
                {instruccionesPortal.map((paso, idx) => (
                  <li key={idx} className="flex gap-3 rounded-sm border border-slate-100 bg-slate-50 p-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0F2D5C] text-white text-xs font-bold">
                      {idx + 1}
                    </span>
                    <span className="leading-relaxed pt-0.5">{paso}</span>
                  </li>
                ))}
              </ol>
              {generatedTxt?.file_path ? (
                <div className="text-[11px] text-slate-600 rounded border border-dashed border-slate-200 p-2 font-mono break-all">
                  Archivo local: {generatedTxt.file_path}
                </div>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-sm"
                onClick={() => window.open(portalUrl, "_blank", "noopener,noreferrer")}
              >
                <ExternalLink className="w-4 h-4 mr-1.5" />
                Abrir portal SAT en nueva pestaña
              </Button>
            </div>
            <div className="flex flex-col bg-slate-100 min-h-[400px] lg:min-h-0">
              <div className="px-3 py-2 bg-slate-800 text-white text-xs flex items-center justify-between gap-2">
                <span className="truncate">Portal SAT — INSC_ASALARIADO</span>
                <a
                  href={portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline shrink-0 hover:text-sky-200"
                >
                  Pantalla completa
                </a>
              </div>
              <iframe
                title="Portal SAT inscripción trabajadores"
                src={portalUrl}
                className="flex-1 w-full min-h-[360px] lg:min-h-[65vh] border-0 bg-white"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              />
              <p className="text-[10px] text-slate-500 px-3 py-2 bg-slate-50 border-t border-slate-200">
                Si el portal no carga aquí (política del SAT), use el enlace «Pantalla completa» o el botón de nueva pestaña.
              </p>
            </div>
          </div>
        ) : null}
      </div>
  );

  return (
    <div className={isTramitesEmbed ? "" : "min-h-screen"}>
      {!isTramitesEmbed ? (
        <PageHeader
          title="Inscripción a Trabajadores SAT"
          subtitle="Capture trabajadores sin RFC, genere el TXT SAT y las cartas PDF de conformidad."
          breadcrumb={["Asistente Laboral y REPSE", "Inscripción de Trabajadores SAT"]}
        />
      ) : null}
      {isTramitesEmbed ? panelBody : <LaboralFlujoOperativo>{panelBody}</LaboralFlujoOperativo>}

      <AlertDialog open={Boolean(success)} onOpenChange={(open) => !open && setSuccess(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archivos generados correctamente</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-700">
                {success?.filename && (
                  <p>
                    TXT: <strong>{success.filename}</strong>
                    {success.worker_count ? ` (${success.worker_count} trabajador(es))` : ""}
                  </p>
                )}
                {success?.conformity_letter_count > 0 && (
                  <p>
                    Cartas PDF: <strong>{success.conformity_letter_count}</strong> archivo(s){" "}
                    <span className="text-xs text-slate-500">(Carta_Conformidad_[CURP].pdf)</span>
                  </p>
                )}
                {Array.isArray(success?.conformity_letters) && success.conformity_letters.length > 0 && (
                  <ul className="text-[11px] text-slate-600 list-disc pl-5 space-y-1 max-h-28 overflow-y-auto">
                    {success.conformity_letters.map((c) => (
                      <li key={c.curp || c.filename}>
                        {c.worker_name || c.curp} — {c.filename}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="break-all rounded border bg-slate-50 p-2 font-mono text-[11px]">
                  {success?.file_path || success?.folder_path || success?.RUTA_ARCHIVOS_GENERALES}
                </p>
                <p className="text-xs text-slate-500">
                  Comprima el .txt en ZIP antes de cargarlo en Mi Portal SAT (INSC_ASALARIADO).
                  Imprima las cartas PDF para firma y huella del trabajador.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={onOpenFolder}>
              <FolderOpen className="w-4 h-4 mr-1" /> Abrir carpeta contenedora
            </Button>
            {success?.filename && (
              <Button type="button" variant="outline" onClick={() => onDownloadFile(success.filename)}>
                Descargar TXT
              </Button>
            )}
            {success?.conformity_letters?.length === 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => onDownloadFile(success.conformity_letters[0].filename)}
              >
                Descargar carta PDF
              </Button>
            )}
            <AlertDialogAction className="bg-[#0F2D5C]">Aceptar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
